import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateLeadRequest,
  LeadSource,
  LeadStatus,
  UpdateLeadRequest,
} from '../api/admin';
import { useAdminApi } from '../api/admin';

type LeadFilters = {
  q: string;
  status: LeadStatus | 'all';
  source: LeadSource | 'all';
  page: number;
  enabled?: boolean;
};

export const LEADS_PAGE_SIZE = 20;

export function useLeads(filters: LeadFilters) {
  const api = useAdminApi();

  return useQuery({
    queryKey: ['admin-leads', filters],
    queryFn: () =>
      api.listLeads({
        q: filters.q || undefined,
        status: filters.status,
        source: filters.source,
        page: filters.page,
      }),
    enabled: filters.enabled ?? true,
    staleTime: 60_000,
  });
}

export function useLead(leadId: string | null) {
  const api = useAdminApi();

  return useQuery({
    queryKey: ['admin-lead', leadId],
    queryFn: () => (leadId ? api.getLead(leadId) : null),
    enabled: Boolean(leadId),
    staleTime: 60_000,
  });
}

export function useCreateLead() {
  const api = useAdminApi();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateLeadRequest) => api.createLead(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-leads'] });
    },
  });
}

export function useUpdateLead() {
  const api = useAdminApi();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ leadId, payload }: { leadId: string; payload: UpdateLeadRequest }) =>
      api.updateLead(leadId, payload),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['admin-leads'] });
      qc.invalidateQueries({ queryKey: ['admin-lead', variables.leadId] });
    },
  });
}

export function useDeleteLead() {
  const api = useAdminApi();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (leadId: string) => api.deleteLead(leadId),
    onSuccess: (_data, leadId) => {
      qc.invalidateQueries({ queryKey: ['admin-leads'] });
      qc.removeQueries({ queryKey: ['admin-lead', leadId] });
    },
  });
}
